/**
 * Lightweight, in-memory error/event logger for the Tulsi Jobs CRM.
 *
 * Why this exists:
 *  - Replaces ad-hoc `console.error` calls with a *visible* log the Owner can see.
 *  - Doubles as a Sentry bridge — when REACT_APP_SENTRY_DSN is set, every error also
 *    goes to Sentry via captureException/captureMessage.
 *
 * Public API:
 *   logError(label, errorOrMessage)   // record an error
 *   logWarn(label, msg)               // record a warning
 *   logInfo(label, msg)               // record an info event (e.g. "Bill emailed")
 *   subscribe(cb)                     // returns unsubscribe(); cb receives the full log array
 *   getLog()                          // current log snapshot (most recent last)
 *   clearLog()                        // wipe in-memory log
 *   markRead()                        // mark current entries as read (used by the badge)
 *   getUnreadCount()                  // for the floating bell badge
 */

import * as Sentry from '@sentry/react';

const MAX_ENTRIES = 100;
let _log = [];
let _readCursor = 0;
const _subs = new Set();

const _notify = () => {
  for (const cb of _subs) {
    try { cb(_log); } catch { /* a subscriber crash must not break logging */ }
  }
};

const _push = (level, label, message, errObj = null) => {
  const entry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    ts: new Date().toISOString(),
    level,
    label: String(label || 'app'),
    message: String(message ?? ''),
    stack: errObj && errObj.stack ? String(errObj.stack).split('\n').slice(0, 5).join('\n') : undefined,
  };
  _log = [..._log, entry].slice(-MAX_ENTRIES);
  _notify();
};

export const logError = (label, errorOrMessage) => {
  const msg = errorOrMessage?.response?.data?.detail
    || errorOrMessage?.message
    || (typeof errorOrMessage === 'string' ? errorOrMessage : String(errorOrMessage));
  _push('error', label, msg, errorOrMessage);
  // Best-effort dev console (helps when DevTools is open)
  // eslint-disable-next-line no-console
  console.error(`[${label}]`, errorOrMessage);
  // Sentry bridge
  if (Sentry.getClient()) {
    if (errorOrMessage instanceof Error) Sentry.captureException(errorOrMessage, { tags: { label } });
    else Sentry.captureMessage(`${label}: ${msg}`, 'error');
  }
};

export const logWarn = (label, message) => {
  _push('warn', label, message);
  // eslint-disable-next-line no-console
  console.warn(`[${label}]`, message);
  if (Sentry.getClient()) Sentry.captureMessage(`${label}: ${message}`, 'warning');
};

export const logInfo = (label, message) => {
  _push('info', label, message);
};

export const subscribe = (cb) => {
  _subs.add(cb);
  cb(_log); // emit immediately so consumers don't flicker
  return () => _subs.delete(cb);
};

export const getLog = () => _log;
export const clearLog = () => { _log = []; _readCursor = 0; _notify(); };
export const markRead = () => { _readCursor = _log.length; _notify(); };
export const getUnreadCount = () => Math.max(0, _log.length - _readCursor);

// Expose on window so it's reachable from the browser console for diagnostics.
// Safe in production: it only contains write-only helpers that the user could already access
// via the Bug-icon panel — this just gives DevTools users a faster path.
if (typeof window !== 'undefined') {
  window.__tjLogger = { logError, logWarn, logInfo, getLog, clearLog };
}
